/**
 * RadialOrbital — projects orbiting a single center bubble.
 *
 * Visual reference: the user-provided RadialOrbitalTimeline pattern (gradient
 * center bubble, ping rings, tethered detail card with status badge + energy
 * bar + connected-nodes section). Reskinned to APETech's OKLCH primary so it
 * doesn't read as the AI-stock purple/cyan gradient.
 *
 * Two surfaces:
 *   - Dashboard / workspace view → center = active workspace, items = projects
 *     in that workspace + pinned-here projects.
 *   - AllProjectsView → center = "All Projects" mark, items = projects across
 *     all workspaces. Per-node ring tinted by home_ws hash so shared projects
 *     group visually.
 *
 * Real data mapped to the reference's status/energy/related slots:
 *   - status → COMPLETE (all tasks done), IN PROGRESS (some open), PENDING (no tasks)
 *   - energy → task done %
 *   - related → projects that share ≥1 member with the active one
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { ProjectAvatar } from '@/components/ProjectAvatar';
import { ArrowRight, Link2, Zap } from 'lucide-react';

export type OrbitalItem = {
  id: string;
  name: string;
  slug: string;
  role: string;
  color: string | null;
  image_key: string | null;
  emoji: string | null;
  task_open: number;
  task_done: number;
  task_total: number;
  members_preview: Array<{ user_id: string; email: string; role: string }>;
  member_count: number;
  /** ISO timestamp (created_at). Used as the "date" pill in the detail card. */
  created_at?: number;
  /** Optional: per-item ring tint (used by AllProjectsView to group by ws). */
  ring_color?: string | null;
  /** For AllProjects shared-from chip in the detail card. */
  home_ws_name?: string | null;
  home_ws_owner_email?: string | null;
  is_mine?: boolean;
};

export type OrbitalCenter =
  | {
      kind: 'workspace';
      name: string;
      image_key: string | null;
      emoji: string | null;
      color: string | null;
    }
  | { kind: 'all'; userInitial: string };

interface Props {
  center: OrbitalCenter;
  items: OrbitalItem[];
  onOpenProject: (id: string) => void;
  emptyCta?: React.ReactNode;
}

const ROTATION_STEP_DEG = 0.3;
const ROTATION_INTERVAL_MS = 50;
const NODE_SIZE = 40;
const NODE_HALO_BASE = 40;

const ROTATION_PREF_KEY = 'apetech-pm:orbital-autorotate';

export function RadialOrbital({ center, items, onOpenProject, emptyCta }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const orbitRef = useRef<HTMLDivElement | null>(null);
  const nodeRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [rotationAngle, setRotationAngle] = useState(0);
  // Default OFF on first visit, persist user preference. Perpetual rotation
  // under the cursor is loud-by-default and re-renders 20fps; users who want
  // it can flip it on and keep it on.
  const [autoRotate, setAutoRotate] = useState<boolean>(() => {
    try {
      return localStorage.getItem(ROTATION_PREF_KEY) === '1';
    } catch {
      return false;
    }
  });
  function setAutoRotatePersist(v: boolean) {
    setAutoRotate(v);
    try {
      localStorage.setItem(ROTATION_PREF_KEY, v ? '1' : '0');
    } catch {
      /* ignore */
    }
  }
  const [activeId, setActiveId] = useState<string | null>(null);
  const [reducedMotion, setReducedMotion] = useState(false);

  // Track viewport width so the orbital radius scales below 640px (375 mobile,
  // 425 large mobile, 520 small tablet). Fixed radius previously clipped nodes
  // off the right edge at 375px and crowded the workspace label.
  const [viewportW, setViewportW] = useState<number>(
    typeof window === 'undefined' ? 1024 : window.innerWidth,
  );
  useEffect(() => {
    function onResize() {
      setViewportW(window.innerWidth);
    }
    window.addEventListener('resize', onResize, { passive: true });
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const radius = useMemo(() => {
    // Cap by viewport: keep ~60px breathing room on each side so the
    // tethered detail card doesn't get cropped at narrow widths.
    const viewportCap = Math.max(110, Math.floor((viewportW - 80) / 2));
    let base: number;
    if (items.length <= 3) base = 160;
    else if (items.length <= 8) base = 200;
    else base = Math.min(280, 200 + (items.length - 8) * 8);
    return Math.min(base, viewportCap);
  }, [items.length, viewportW]);

  // Pre-compute "related" map: for each item, IDs of other items sharing ≥1 member.
  // This is the analog of the reference's `relatedIds` field — projects with
  // overlapping people are the meaningful relationship in our domain.
  const relatedMap = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const a of items) {
      const ids: string[] = [];
      const aUserIds = new Set(a.members_preview.map((mm) => mm.user_id));
      for (const b of items) {
        if (b.id === a.id) continue;
        if (b.members_preview.some((mm) => aUserIds.has(mm.user_id))) ids.push(b.id);
      }
      m.set(a.id, ids);
    }
    return m;
  }, [items]);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Auto-rotate via requestAnimationFrame so the loop pauses naturally when
  // the tab is hidden (browsers throttle rAF on background tabs). Previous
  // setInterval(50ms) ran 20fps forever AND triggered a React re-render on
  // every tick; now we accumulate angle in a ref + setState at most ~30fps,
  // and the loop self-terminates on visibility change via document.hidden.
  useEffect(() => {
    if (reducedMotion || !autoRotate) return;
    let raf = 0;
    let last = performance.now();
    const DEG_PER_MS = ROTATION_STEP_DEG / ROTATION_INTERVAL_MS;
    function tick(now: number) {
      const delta = now - last;
      last = now;
      if (!document.hidden) {
        setRotationAngle((a) => Number(((a + delta * DEG_PER_MS) % 360).toFixed(3)));
      }
      raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame((t) => {
      last = t;
      tick(t);
    });
    return () => cancelAnimationFrame(raf);
  }, [autoRotate, reducedMotion]);

  // Keyboard nav: SCOPED to the orbital container (was on window — swallowed
  // ArrowLeft/Right + Escape for any input on the page; the
  // HTMLInputElement/Textarea guard missed contenteditable / select / etc).
  // Container has tabIndex so it can receive focus delegation from its
  // children; keydown bubbles up from the focused node within.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setActiveId(null);
        // Don't force auto-rotate back on after Escape — user explicitly
        // dismissed something, not a signal they want motion resumed.
        return;
      }
      if (!autoRotate || reducedMotion) {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          setRotationAngle((a) => (a - 6 + 360) % 360);
        }
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          setRotationAngle((a) => (a + 6) % 360);
        }
      }
    }
    el.addEventListener('keydown', onKey);
    return () => el.removeEventListener('keydown', onKey);
  }, [autoRotate, reducedMotion]);

  // Auto-pause auto-rotation when keyboard focus enters the orbital — the
  // moving target is unusable for keyboard users. Resume only via explicit
  // pause-toggle click.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    function onFocusIn() {
      if (autoRotate) setAutoRotatePersist(false);
    }
    el.addEventListener('focusin', onFocusIn);
    return () => el.removeEventListener('focusin', onFocusIn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRotate]);

  function toggleNode(id: string) {
    if (activeId === id) {
      setActiveId(null);
      setAutoRotatePersist(true);
      return;
    }
    setActiveId(id);
    setAutoRotatePersist(false);
    const idx = items.findIndex((i) => i.id === id);
    if (idx >= 0) {
      const targetAngle = (idx / items.length) * 360;
      setRotationAngle((270 - targetAngle + 360) % 360);
    }
  }

  function nodePosition(index: number) {
    const angle = ((index / items.length) * 360 + rotationAngle) % 360;
    const rad = (angle * Math.PI) / 180;
    const x = radius * Math.cos(rad);
    const y = radius * Math.sin(rad);
    const zIndex = Math.round(100 + 50 * Math.cos(rad));
    const opacity = Math.max(0.4, Math.min(1, 0.4 + 0.6 * ((1 + Math.sin(rad)) / 2)));
    return { x, y, angle, zIndex, opacity };
  }

  function isRelatedToActive(id: string): boolean {
    if (!activeId) return false;
    return relatedMap.get(activeId)?.includes(id) ?? false;
  }

  function statusOf(item: OrbitalItem): 'completed' | 'in-progress' | 'pending' {
    if (item.task_total === 0) return 'pending';
    if (item.task_open === 0) return 'completed';
    return 'in-progress';
  }

  function statusBadgeClasses(status: 'completed' | 'in-progress' | 'pending') {
    switch (status) {
      case 'completed':
        return 'bg-foreground text-background border-foreground';
      case 'in-progress':
        return 'bg-background text-foreground border-foreground';
      case 'pending':
        return 'bg-foreground/20 text-foreground border-foreground/40';
    }
  }
  function statusLabel(status: 'completed' | 'in-progress' | 'pending') {
    return status === 'completed' ? 'COMPLETE' : status === 'in-progress' ? 'IN PROGRESS' : 'PENDING';
  }

  function dateString(ms?: number): string {
    if (!ms) return '';
    return new Date(ms).toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
  }

  return (
    <section
      ref={containerRef}
      aria-label="Project orbit"
      onClick={(e) => {
        if (e.target === containerRef.current || e.target === orbitRef.current) {
          setActiveId(null);
          setAutoRotatePersist(true);
        }
      }}
      className="relative grid min-h-[460px] w-full place-items-center overflow-hidden rounded-2xl border border-border bg-background sm:min-h-[540px] md:min-h-[640px]"
    >
      {/* Ambient mesh gradient orb (subtle) — single OKLCH primary, no AI mix */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(circle at 50% 50%, color-mix(in oklch, var(--primary) 10%, transparent) 0%, transparent 60%)',
        }}
      />

      {/* Pause/rotate toggle */}
      <div className="absolute right-4 top-4 z-30">
        <button
          type="button"
          onClick={() => setAutoRotatePersist(!autoRotate)}
          disabled={reducedMotion}
          className="rounded-full border border-border bg-card/80 px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground backdrop-blur-sm transition-colors hover:text-foreground disabled:opacity-60"
          aria-label={
            reducedMotion
              ? 'Auto-rotation disabled by reduced-motion preference'
              : autoRotate
                ? 'Pause auto-rotation'
                : 'Resume auto-rotation'
          }
          aria-pressed={!reducedMotion ? autoRotate : undefined}
        >
          {reducedMotion ? 'static' : autoRotate ? 'rotating' : 'paused'}
        </button>
      </div>

      {items.length === 0 ? (
        <div className="z-10 flex max-w-sm flex-col items-center gap-5 px-6 text-center">
          {center.kind === 'workspace' ? (
            <ProjectAvatar
              name={center.name}
              image_key={center.image_key}
              emoji={center.emoji}
              color={center.color}
              size="lg"
              shape="rounded"
            />
          ) : (
            <div className="grid size-14 place-items-center rounded-full border border-border bg-card text-lg font-bold text-foreground">
              {center.userInitial}
            </div>
          )}
          <div className="space-y-1.5">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              {center.kind === 'workspace' ? center.name : 'All projects'}
            </div>
            <div className="text-base text-foreground">No projects yet</div>
            <div className="text-sm text-muted-foreground">
              Spin one up to start triaging events, tasks, and notes.
            </div>
          </div>
          {emptyCta}
        </div>
      ) : (
        <div
          ref={orbitRef}
          className="relative grid place-items-center"
          style={{
            width: `${radius * 2 + 120}px`,
            height: `${radius * 2 + 120}px`,
            perspective: '1000px',
          }}
        >
          {/* Concentric orbit ring */}
          <div
            aria-hidden
            className="pointer-events-none absolute rounded-full border border-foreground/10"
            style={{ width: `${radius * 2}px`, height: `${radius * 2}px` }}
          />

          {/* Center bubble — gradient + ping rings (reskinned OKLCH primary) */}
          <CenterBubble center={center} pulsing={!reducedMotion} />

          {items.map((item, idx) => {
            const pos = nodePosition(idx);
            const isActive = item.id === activeId;
            const isRelated = isRelatedToActive(item.id);
            const status = statusOf(item);
            const haloSize = NODE_HALO_BASE + (item.task_total > 0 ? item.task_done * 6 : 8);
            const detailId = `orbital-detail-${item.id}`;
            const tint = item.ring_color ?? 'var(--primary)';

            return (
              <div
                key={item.id}
                ref={(el) => (nodeRefs.current[item.id] = el)}
                className="absolute"
                style={{
                  transform: `translate(${pos.x}px, ${pos.y}px)`,
                  zIndex: isActive ? 200 : pos.zIndex,
                  opacity: isActive ? 1 : pos.opacity,
                  transition: reducedMotion
                    ? 'none'
                    : 'transform 700ms cubic-bezier(0.32,0.72,0,1), opacity 400ms ease-out',
                }}
              >
                {/* Soft halo — energy-like glow, tinted to the ring/primary */}
                <div
                  aria-hidden
                  className={cn(
                    'pointer-events-none absolute rounded-full',
                    isRelated && !reducedMotion ? 'animate-pulse [animation-duration:1.4s]' : '',
                  )}
                  style={{
                    width: `${haloSize}px`,
                    height: `${haloSize}px`,
                    left: `-${(haloSize - NODE_SIZE) / 2}px`,
                    top: `-${(haloSize - NODE_SIZE) / 2}px`,
                    background: `radial-gradient(circle, color-mix(in oklch, ${tint} 30%, transparent) 0%, transparent 70%)`,
                  }}
                />

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleNode(item.id);
                  }}
                  aria-label={
                    item.task_total === 0
                      ? `${item.name}, no tasks yet`
                      : `${item.name} — ${item.task_open} open of ${item.task_total} task${item.task_total === 1 ? '' : 's'}`
                  }
                  aria-expanded={isActive}
                  aria-controls={isActive ? detailId : undefined}
                  className={cn(
                    'relative grid place-items-center rounded-full border-2 active:scale-95',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                    isActive
                      ? 'bg-foreground text-background border-foreground'
                      : isRelated
                        ? 'bg-foreground/60 text-background border-foreground'
                        : 'bg-card text-foreground border-foreground/40',
                  )}
                  style={{
                    width: `${NODE_SIZE}px`,
                    height: `${NODE_SIZE}px`,
                    transform: isActive ? 'scale(1.5)' : 'scale(1)',
                    boxShadow: isActive
                      ? `0 0 0 1px ${tint}, 0 16px 40px -8px color-mix(in oklch, ${tint} 50%, transparent)`
                      : `0 4px 12px -6px color-mix(in oklch, var(--background) 80%, transparent)`,
                    transition: reducedMotion
                      ? 'none'
                      : 'transform 320ms cubic-bezier(0.32,0.72,0,1), background-color 240ms ease-out, border-color 240ms ease-out, box-shadow 240ms ease-out',
                  }}
                >
                  <ProjectAvatar
                    name={item.name}
                    image_key={item.image_key}
                    emoji={item.emoji}
                    color={item.color}
                    size="xs"
                    shape="rounded"
                    className="!size-7"
                  />
                </button>

                {/* Label — hidden for nodes that are deep on the back-arc
                    (opacity < 0.55) since they overlap each other into noise
                    at 8+ projects. Active node always renders the label. */}
                {(isActive || pos.opacity >= 0.55) && (
                  <div
                    className={cn(
                      'pointer-events-none absolute left-1/2 top-[calc(100%+8px)] -translate-x-1/2 whitespace-nowrap font-semibold tracking-wider transition-all',
                      isActive
                        ? 'scale-110 text-[11px] text-foreground'
                        : 'text-[10px] text-foreground/70',
                    )}
                    style={{ textShadow: '0 1px 2px var(--background)' }}
                  >
                    {item.name}
                  </div>
                )}

                {/* Detail card tethered below the node. Card now uses a
                    flex column with a scrollable body and a STICKY footer
                    holding the Open-project CTA — at high project counts the
                    Connected-projects list grew tall enough to push the CTA
                    below the orbital container (clipped, unreachable).
                    max-h ties to viewport so the card never extends past
                    visible bounds; body scrolls internally. */}
                {isActive && (
                  <div
                    id={detailId}
                    role="region"
                    aria-labelledby={`${detailId}-title`}
                    className="absolute left-1/2 top-[80px] z-[300] flex w-72 max-h-[min(560px,calc(100vh-120px))] -translate-x-1/2 flex-col overflow-hidden rounded-lg border border-foreground/30 bg-card/95 p-0 shadow-2xl backdrop-blur-xl animate-in fade-in-0 zoom-in-95 duration-200"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {/* Tether line from node down to card */}
                    <div
                      aria-hidden
                      className="absolute -top-3 left-1/2 h-3 w-px -translate-x-1/2"
                      style={{ background: 'color-mix(in oklch, var(--foreground) 50%, transparent)' }}
                    />

                    {/* Header: status badge + date */}
                    <div className="space-y-2 px-4 pt-4 pb-2">
                      <div className="flex items-center justify-between gap-2">
                        <span
                          className={cn(
                            'inline-flex items-center rounded-full border px-2 py-0.5 font-mono text-[9px] font-semibold tracking-widest',
                            statusBadgeClasses(status),
                          )}
                        >
                          {statusLabel(status)}
                        </span>
                        {item.created_at && (
                          <span className="font-mono text-[10px] text-muted-foreground">
                            {dateString(item.created_at)}
                          </span>
                        )}
                      </div>
                      <div className="flex items-start gap-2">
                        <ProjectAvatar
                          name={item.name}
                          image_key={item.image_key}
                          emoji={item.emoji}
                          color={item.color}
                          size="sm"
                          shape="rounded"
                        />
                        <div className="min-w-0 flex-1">
                          <h3
                            id={`${detailId}-title`}
                            className="truncate text-sm font-semibold tracking-tight"
                          >
                            {item.name}
                          </h3>
                          <p className="truncate font-mono text-[10px] text-muted-foreground">
                            /p/{item.slug} · {item.role}
                          </p>
                        </div>
                      </div>
                      {item.home_ws_name && !item.is_mine && (
                        <p className="font-mono text-[10px] text-muted-foreground">
                          Shared from{' '}
                          <span className="text-foreground">{item.home_ws_name}</span>
                          {item.home_ws_owner_email && (
                            <>
                              {' · '}
                              <span className="text-foreground">{item.home_ws_owner_email}</span>
                            </>
                          )}
                        </p>
                      )}
                    </div>

                    {/* Body — scrollable so tall content (high connectivity
                        in dense workspaces) doesn't push the CTA off-screen. */}
                    <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 pb-3">
                      {/* Progress (energy analog) */}
                      <div className="border-t border-foreground/10 pt-3">
                        <div className="mb-1 flex items-center justify-between text-[10px]">
                          <span className="inline-flex items-center text-foreground/80">
                            <Zap size={10} className="mr-1" />
                            Progress
                          </span>
                          <span className="font-mono text-muted-foreground">
                            {item.task_total > 0
                              ? `${Math.round((item.task_done / item.task_total) * 100)}% · ${item.task_done}/${item.task_total}`
                              : 'No tasks'}
                          </span>
                        </div>
                        <div
                          className="h-1 w-full overflow-hidden rounded-full bg-foreground/10"
                          role="progressbar"
                          aria-valuenow={
                            item.task_total > 0
                              ? Math.round((item.task_done / item.task_total) * 100)
                              : 0
                          }
                          aria-valuemin={0}
                          aria-valuemax={100}
                        >
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${item.task_total > 0 ? Math.round((item.task_done / item.task_total) * 100) : 0}%`,
                              background: `linear-gradient(90deg, color-mix(in oklch, var(--primary) 95%, transparent), color-mix(in oklch, var(--primary) 65%, transparent))`,
                              transition: reducedMotion
                                ? 'none'
                                : 'width 600ms cubic-bezier(0.32,0.72,0,1)',
                            }}
                          />
                        </div>
                      </div>

                      {/* Members */}
                      {item.member_count > 0 && (
                        <div className="border-t border-foreground/10 pt-3">
                          <div className="mb-2 flex items-center text-[10px] uppercase tracking-widest text-muted-foreground">
                            <Link2 size={10} className="mr-1" />
                            Members
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="flex -space-x-1.5">
                              {item.members_preview.map((m) => (
                                <span
                                  key={m.user_id}
                                  className="grid size-6 place-items-center rounded-full bg-secondary text-[10px] font-semibold text-secondary-foreground ring-2 ring-card"
                                  title={`${m.email} (${m.role})`}
                                >
                                  {m.email[0]?.toUpperCase()}
                                </span>
                              ))}
                              {item.member_count > item.members_preview.length && (
                                <span className="grid size-6 place-items-center rounded-full bg-foreground/10 font-mono text-[10px] text-muted-foreground ring-2 ring-card">
                                  +{item.member_count - item.members_preview.length}
                                </span>
                              )}
                            </div>
                            <span className="font-mono text-[10px] text-muted-foreground">
                              {item.member_count}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Connected projects — cap to first 6 with a "+N more"
                          chip so dense workspaces (every project shares the
                          owner = N² connectivity) don't explode the card. */}
                      {(() => {
                        const related = relatedMap.get(item.id) ?? [];
                        if (related.length === 0) return null;
                        const visible = related.slice(0, 6);
                        const hiddenCount = related.length - visible.length;
                        return (
                          <div className="border-t border-foreground/10 pt-3">
                            <div className="mb-2 flex items-center text-[10px] uppercase tracking-widest text-muted-foreground">
                              <Link2 size={10} className="mr-1" />
                              Connected projects
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {visible.map((relId) => {
                                const r = items.find((i) => i.id === relId);
                                if (!r) return null;
                                return (
                                  <button
                                    key={relId}
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleNode(relId);
                                    }}
                                    className="inline-flex items-center gap-1 rounded-sm border border-foreground/20 bg-transparent px-2 py-0.5 text-[10px] text-foreground/80 transition-colors hover:bg-foreground/10 hover:text-foreground"
                                  >
                                    <span className="max-w-[140px] truncate">{r.name}</span>
                                    <ArrowRight size={8} className="text-foreground/60 shrink-0" />
                                  </button>
                                );
                              })}
                              {hiddenCount > 0 && (
                                <span
                                  className="inline-flex items-center rounded-sm border border-foreground/10 bg-foreground/5 px-2 py-0.5 font-mono text-[10px] text-muted-foreground"
                                  title={`Plus ${hiddenCount} more connected via shared members`}
                                >
                                  +{hiddenCount}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                    </div>

                    {/* Sticky CTA footer — always reachable regardless of body
                        scroll position. Top border separates from scrolled
                        content so it doesn't visually merge. */}
                    <div className="shrink-0 border-t border-foreground/10 bg-card/95 p-3 backdrop-blur-xl">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenProject(item.id);
                        }}
                        className="w-full rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary/90 active:scale-[0.98]"
                      >
                        Open project <span aria-hidden>→</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

/**
 * Center bubble — keeps the user-requested reference look (gradient bubble +
 * two ping rings + inner core) but reskinned to APETech's OKLCH primary so
 * it doesn't read as the AI-stock purple→blue→teal gradient.
 */
function CenterBubble({ center, pulsing }: { center: OrbitalCenter; pulsing: boolean }) {
  return (
    <div className="absolute z-10 grid size-16 place-items-center">
      {/* ONE slow ping ring — two concurrent rings + pulse + gradient + 20fps
          rotation was four loops under the cursor at once, fighting the
          "calm, hardware-precise" voice. One ring at 4s reads as ambient
          status, not AI-dashboard cosplay. */}
      {pulsing && (
        <div
          aria-hidden
          className="absolute size-20 rounded-full border opacity-60 animate-ping [animation-duration:4s]"
          style={{ borderColor: 'color-mix(in oklch, var(--primary) 25%, transparent)' }}
        />
      )}

      {/* Base — OKLCH primary fill, no pulse loop (pulse + ping + rotation
          was redundant motion). */}
      <div
        className={cn('absolute inset-0 flex items-center justify-center rounded-full')}
        style={{
          background:
            'linear-gradient(135deg, color-mix(in oklch, var(--primary) 95%, transparent), color-mix(in oklch, var(--primary) 60%, transparent) 60%, color-mix(in oklch, var(--primary) 35%, transparent))',
          boxShadow:
            'inset 0 1px 0 color-mix(in oklch, var(--foreground) 14%, transparent), 0 18px 40px -10px color-mix(in oklch, var(--primary) 50%, transparent)',
        }}
        aria-hidden
      />

      {/* Inner core — the actual avatar / monogram */}
      {center.kind === 'workspace' ? (
        <div className="relative grid size-8 place-items-center rounded-full bg-card backdrop-blur-md">
          <ProjectAvatar
            name={center.name}
            image_key={center.image_key}
            emoji={center.emoji}
            color={center.color}
            size="xs"
            shape="rounded"
            className="!size-7"
          />
        </div>
      ) : (
        <div
          className="relative grid size-8 place-items-center rounded-full text-sm font-bold backdrop-blur-md"
          style={{
            background: 'color-mix(in oklch, var(--card) 85%, transparent)',
            color: 'var(--foreground)',
          }}
        >
          {center.userInitial}
        </div>
      )}

      {/* Label below */}
      <div className="pointer-events-none absolute top-[calc(100%+12px)] whitespace-nowrap text-center">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
          {center.kind === 'workspace' ? center.name : 'All projects'}
        </div>
      </div>
    </div>
  );
}
